import { OfficeDetail } from "./models/office/OfficeDetail";
import { OfficeTracker } from "./utils/office-tracker";
import { MDCDrawer } from "@material/drawer";
import { MDCTopAppBar } from '@material/top-app-bar';

export class AgencyPrincipalNavigation {
    public static cssClass: string = 'agency-header';
    private officeTracker: OfficeTracker = new OfficeTracker();
    public office: OfficeDetail;

    constructor(private rootElement: HTMLDivElement) {
        this.showAP(false, this.officeTracker.hasCustomHeader);
        if (this.officeTracker.hasCustomHeader) {
            this.load();
        }
    }

    private load = async (): Promise<void> => {
        if (window.localStorage.office) {
            try {
                this.office = OfficeDetail.fromJs(JSON.parse(window.localStorage.office));
                this.setAP();
            } catch (e) {
                console.warn('Unable to use cached office.', e);
            }
        }

        const response = await fetch('/api/officeinfo');
        const responseContent = await response.json();
        if (responseContent) {
            window.localStorage.office = JSON.stringify(responseContent);
            this.office = OfficeDetail.fromJs(responseContent);
            this.setAP();
        }
    }

    private setAP = (): void => {
        if (!this.office) {
            this.showAP(false);
        } else if (this.office.agencyPrincipalIndicator) {
            this.setLogo();
            this.setAboutLink();
            this.setGetAQuoteLink();
            this.showAP(true);
        }
    }

    private showAP = (ap: boolean, hasHeader?: boolean): void => {
        const apNavDiv = document.querySelector('.agency-header') as HTMLDivElement;
        const apMblNavDiv = document.querySelector('.agency-mobile-header') as HTMLDivElement;
        const primaryNavDiv = document.querySelector('.primary-header') as HTMLDivElement;
        const primaryMblNavDiv = document.querySelector('.primary-mobile-header') as HTMLDivElement;
        const apDrawer = document.querySelector('.agency-drawer') as HTMLElement;
        const primaryDrawer = document.querySelector('.primary-drawer') as HTMLElement;
        const apDrawerScrim = document.querySelector('#agency-scrim') as HTMLElement;
        const primaryDrawerScrim = document.querySelector('#primary-scrim') as HTMLElement;

        if (hasHeader || (this.office?.agencyPrincipalIndicator && ap)) {
            if (apNavDiv) apNavDiv.hidden = false;
            if (apMblNavDiv) apMblNavDiv.hidden = false;
            if (apDrawer) apDrawer.hidden = false;
            if (primaryNavDiv) primaryNavDiv.hidden = true;
            if (primaryMblNavDiv) primaryMblNavDiv.hidden = true;
            if (primaryDrawer) primaryDrawer.hidden = true;
            if (apDrawerScrim) apDrawerScrim.classList.add('mdc-drawer-scrim');
            this.setAPMobileLinks();
            if (!this.isAgencyPage()) {
                this.setNavBackground();
            }
            this.configureAgencyDrawer();
        } else {
            if (apNavDiv) apNavDiv.hidden = true;
            if (apMblNavDiv) apMblNavDiv.hidden = true;
            if (apDrawer) apDrawer.hidden = true;
            if (primaryDrawer) primaryDrawer.hidden = false;
            if (primaryNavDiv) primaryNavDiv.hidden = false;
            if (primaryMblNavDiv) primaryMblNavDiv.hidden = false;
            if (primaryDrawerScrim) primaryDrawerScrim.classList.add('mdc-drawer-scrim');
            this.configurePrimaryDrawer();
            const searchMenuPHMobile = document.getElementsByClassName('searchMenuPHMobile')[0]?.parentElement;
            if (searchMenuPHMobile) searchMenuPHMobile.style.display = "block";
        }
    }

    private setLogo = (): void => {
        const logo = this.rootElement.querySelector('.f-ifb-logo') as HTMLElement;
        if (logo) {
            logo.style.backgroundImage = `url('${this.office.logoUrl}')`;
            const logoAnchor = logo.parentElement as HTMLAnchorElement;
            if (logoAnchor) {
                logoAnchor.href = this.office.agencyUrl;
            }
        }

        const mbllogo = document.querySelector('#agency-mobile-logo') as HTMLElement;
        if (mbllogo) {
            mbllogo.style.backgroundImage = `url('${this.office.logoUrl}')`;
            const mblLogoAnchor = mbllogo.parentElement as HTMLAnchorElement;
            if (mblLogoAnchor) {
                mblLogoAnchor.href = this.office.agencyUrl;
            }
        }
    }

    private setAboutLink = (): void => {
        const navButtons = this.rootElement.querySelectorAll('a.mdc-button');
        if (navButtons) {
            const aboutLink = Array.from(navButtons).find((x: HTMLAnchorElement) =>
                x.href?.includes('company')) as HTMLAnchorElement;
            if (aboutLink) {
                aboutLink.href = this.office.agencyUrl;
            }
        }
    }

    private setGetAQuoteLink = (): void => {
        const navButtons = this.rootElement.querySelectorAll('a.mdc-button');
        if (navButtons) {
            const quoteLink = Array.from(navButtons).find((x: HTMLAnchorElement) =>
                x.href?.includes('insurance-bundle') || x.href?.includes('get-a-quote')) as HTMLAnchorElement;
            if (quoteLink) {
                quoteLink.href = `${this.office?.agencyUrl}#get-a-quote`;
            }
        }
    }

    private isAgencyPage = (): boolean => {
        return new RegExp('(\/[Oo]ffices\/).*.(-[Aa]gency)').test(document.location.href);
    }

    private setNavBackground = (): void => {
        const navbar = this.rootElement.querySelector('.agency-header #agency-navbar') as HTMLDivElement;
        if (navbar) {
            navbar.style.background = "#f9e7d9";
            navbar.style.boxShadow = "0px 0px 0px transparent";
        }
    }

    private setAPMobileLinks = (): void => {
        const searchMenuPHMobile = document.getElementsByClassName('searchMenuPHMobile')[0]?.parentElement;
        if (searchMenuPHMobile) searchMenuPHMobile.style.display = "none";

        const navEls = document.querySelectorAll('.agency-drawer nav.mdc-list .mdc-list-item__text');
        if (navEls) {
            const findAgentA = Array.from(navEls).find((x: HTMLElement) =>
                x.textContent?.toLowerCase().includes('find an agent')) as HTMLElement;
            if (findAgentA) {
                findAgentA.parentElement?.style.setProperty("display", "none");
            }

            const companyA = Array.from(navEls).find((x: HTMLElement) =>
                x.textContent?.toLowerCase().includes('company')) as HTMLElement;
            if (companyA) {
                const parentEl = companyA.parentElement as HTMLAnchorElement;
                if (parentEl && this.office) {
                    parentEl.setAttribute('href', this.office.agencyUrl);
                    companyA.innerText = "About";
                }
            }

            const quoteA = Array.from(navEls).find((x: HTMLElement) =>
                x.textContent?.toLowerCase().includes('get a quote')) as HTMLElement;
            if (quoteA) {
                const parentEl = quoteA.parentElement as HTMLAnchorElement;
                if (parentEl) {
                    parentEl.setAttribute('href', `${this.office.agencyUrl}#get-a-quote`);
                }
            }
        }
    }

    getAgencyUrl = (): string => this.office?.agencyUrl;

    configurePrimaryDrawer = (): void => {
        const drawerEl = document.querySelector(".mdc-drawer.primary-drawer");
        if (drawerEl) {
            const drawer = MDCDrawer.attachTo(drawerEl);
            const topAppBarEl = document.querySelector(".mdc-top-app-bar#primary-navbar");
            const topAppBar = MDCTopAppBar.attachTo(topAppBarEl);
            topAppBar.setScrollTarget(document.querySelector(".main-content"));
            topAppBar.listen("MDCTopAppBar:nav", () => {
                drawer.open = !drawer.open;
            });
        }
    }

    configureAgencyDrawer = (): void => {
        const drawerEl = document.querySelector(".mdc-drawer.agency-drawer");
        if (drawerEl) {
            const drawer = MDCDrawer.attachTo(drawerEl);
            const topAppBarEl = document.querySelector(".mdc-top-app-bar#agency-navbar");
            const topAppBar = MDCTopAppBar.attachTo(topAppBarEl);
            topAppBar.setScrollTarget(document.querySelector(".main-content"));
            topAppBar.listen("MDCTopAppBar:nav", () => {
                drawer.open = !drawer.open;
            });
        }

        const sideNavEls = document.querySelectorAll(".nav-item");
        if (sideNavEls) {
            const findAgentA = Array.from(sideNavEls).find((x) =>
                x.textContent?.toLowerCase().includes('find agent')) as HTMLElement;
            if (findAgentA) {
                findAgentA.style.setProperty("display", "none");
            }
        }

        const bottomNavEls = document.querySelectorAll(".bar-item");
        if (bottomNavEls) {
            const findAgentA = Array.from(bottomNavEls).find((x) =>
                x.textContent?.toLowerCase().includes('find agent')) as HTMLElement;
            if (findAgentA) {
                findAgentA.style.setProperty("display", "none");
            }
        }

        const dynamicMblNavs = document.querySelectorAll("#drawerDynamicContent .mdc-list .mdc-list-item");
        if (dynamicMblNavs) {
            const findAgentA = Array.from(dynamicMblNavs).find((x) =>
                x.textContent?.toLowerCase().includes('find agent')) as HTMLElement;
            if (findAgentA) {
                findAgentA.style.setProperty("display", "none");
            }
        }
    }
}